<script setup lang="ts">
import { defineAsyncComponent, defineComponent, ref } from 'vue'

import type { RichTextEditorProps } from './types'

import { useToast } from '@/shared/ui/feedback'
import { Textarea } from '@/shared/ui/textarea'

// Lazy public entry for the tiptap editor. This wrapper stays light (NO tiptap import): the heavy
// implementation is pulled via defineAsyncComponent, so tiptap + StarterKit split into their own
// Vite chunk and never enter the main bundle (contract 02 §4, §16).
//
// Contract 02 §14: a lazy-chunk load failure must degrade to a <Textarea> + error toast — never a
// blank pane. defineAsyncComponent's errorComponent gets none of the original props, so the
// fallback is owned HERE where modelValue stays bound.
const model = defineModel<string>({ default: '' })

const props = withDefaults(defineProps<RichTextEditorProps>(), {
  label: '',
  placeholder: '',
  minHeight: '7.5rem',
  maxHeight: '18.75rem',
  loadErrorMessage: 'ui.editor.loadError',
  disabled: false,
})

const loadFailed = ref(false)
const toast = useToast()

// Rendered only if the chunk errors before loadFailed swaps the template to the fallback.
const EmptyFailure = defineComponent({ name: 'RichTextEditorLoadError', render: () => null })

const TiptapEditor = defineAsyncComponent({
  loader: () => import('./TiptapEditor.vue'),
  errorComponent: EmptyFailure,
  onError(_error, _retry, fail) {
    loadFailed.value = true
    toast.error(props.loadErrorMessage)
    fail()
  },
})
</script>

<template>
  <Textarea
    v-if="loadFailed"
    v-model="model"
    :label="label"
    :placeholder="placeholder"
    :disabled="disabled"
  />
  <TiptapEditor
    v-else
    v-model="model"
    :label="label"
    :toolbar-labels="toolbarLabels"
    :min-height="minHeight"
    :max-height="maxHeight"
    :disabled="disabled"
  />
</template>
